import { AlertTriangle, Inbox, Lock, SearchX, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

/**
 * The six states every data view must implement.
 *
 * These are built in Phase 1, before any feature screen, because they are exactly what
 * gets skipped under time pressure — and an empty table that looks identical to a
 * filtered one, or to a failed request, is how users lose trust in the data.
 *
 * NoResults is deliberately distinct from Empty: "no data at all" and "no matches for
 * your filters" require different actions from the user.
 */

interface StateShellProps {
  icon: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
  tone?: 'neutral' | 'danger';
}

function StateShell({ icon, title, description, action, className, tone = 'neutral' }: StateShellProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-16 text-center',
        className,
      )}
    >
      <div
        aria-hidden
        className={cn(
          'flex size-10 items-center justify-center rounded-full',
          tone === 'danger' ? 'bg-danger-bg text-danger' : 'bg-subtle text-fg-subtle',
        )}
      >
        {icon}
      </div>
      <div className="space-y-1">
        <p className="text-body font-medium text-fg">{title}</p>
        {description && (
          <p className="mx-auto max-w-sm text-caption text-fg-muted">{description}</p>
        )}
      </div>
      {action}
    </div>
  );
}

/** No data exists yet. Offer the action that creates the first record. */
export function EmptyState({
  title = 'Nothing here yet',
  description,
  action,
  className,
}: {
  title?: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <StateShell
      icon={<Inbox className="size-5" />}
      title={title}
      description={description}
      action={action}
      className={className}
    />
  );
}

/** Data exists, but the current filters match none of it. Always offer a way back. */
export function NoResultsState({
  onClearFilters,
  className,
}: {
  onClearFilters?: () => void;
  className?: string;
}) {
  return (
    <StateShell
      icon={<SearchX className="size-5" />}
      title="No matching results"
      description="No records match the current search and filters."
      action={
        onClearFilters && (
          <Button variant="secondary" size="sm" onClick={onClearFilters}>
            Clear filters
          </Button>
        )
      }
      className={className}
    />
  );
}

/** The request failed. Say so, and let the user retry. */
export function ErrorState({
  title = 'Could not load this',
  description = 'Something went wrong while loading. Please try again.',
  onRetry,
  action,
  className,
}: {
  title?: string;
  description?: string;
  onRetry?: () => void;
  /** Overrides the default Try again button when set. */
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <StateShell
      tone="danger"
      icon={<AlertTriangle className="size-5" />}
      title={title}
      description={description}
      action={
        action ??
        (onRetry && (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Try again
          </Button>
        ))
      }
      className={className}
    />
  );
}

/**
 * The user is authenticated but lacks the permission.
 *
 * It names what is missing and who to ask, rather than showing an empty shell — which
 * leaves the user unsure whether the feature is broken or forbidden.
 */
export function ForbiddenState({
  what = 'this page',
  className,
}: {
  what?: string;
  className?: string;
}) {
  return (
    <StateShell
      icon={<Lock className="size-5" />}
      title="You do not have access"
      description={`Your role does not include permission to view ${what}. Contact your administrator if you believe this is wrong.`}
      className={className}
    />
  );
}

export function OfflineBanner({ className }: { className?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex items-center justify-center gap-2 bg-warning-bg px-4 py-2 text-caption text-warning',
        className,
      )}
    >
      <WifiOff aria-hidden className="size-4" />
      You are offline. Showing the last loaded data — changes are disabled until you
      reconnect.
    </div>
  );
}

/**
 * Skeletons match the real row height and column widths. A spinner in a table collapses
 * the layout and then reflows it, which is worse than a brief grey placeholder.
 */
export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn('animate-pulse rounded bg-subtle', className)} />;
}

export function TableSkeleton({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div role="status" aria-label="Loading" className="divide-y divide-line">
      {/* Index keys are correct here: these are fixed placeholder rows with no identity
          and no reordering — the condition the rule exists to catch. */}
      {/* oxlint-disable react/no-array-index-key */}
      {Array.from({ length: rows }).map((_row, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4 px-4 py-3">
          {Array.from({ length: columns }).map((_cell, colIndex) => (
            <Skeleton key={colIndex} className={cn('h-4', colIndex === 0 ? 'w-40' : 'w-24')} />
          ))}
        </div>
      ))}
      {/* oxlint-enable react/no-array-index-key */}
    </div>
  );
}
